/**
 * SceneForge V5 — Active team context.
 *
 * Single source of truth for "which team am I acting as right now?"
 * Persists to localStorage so a hard reload doesn't bounce the user
 * back through the team selector. Mirrors the active team_id into
 * `lib/api.ts`'s `setActiveTeamId(...)` so every API call auto-attaches
 * the `X-Sceneforge-Team` header.
 *
 * The provider is mounted high in the tree (above the router) by
 * `App.tsx`. `RequireSceneForgeAccess` reads the membership list off
 * `me.team_memberships` and, on first render after sign-in, either:
 *
 *   - admin with 0 teams        → routes to `/admin` (must create a team)
 *   - exactly 1 membership      → auto-selects that team
 *   - 2+ memberships, no active → routes to `/team-select`
 *   - persisted team_id stale   → clears it and falls back to above
 *
 * All membership-based logic lives in `RequireSceneForgeAccess.tsx`; this
 * file owns only the state container + the header bridge.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import { setActiveTeamId as setApiTeamId } from './api';
import type { TeamContext } from './types';

const STORAGE_KEY = 'sceneforge.activeTeam';

interface TeamContextValue {
  /** The currently-selected team, or null when no team is active. */
  activeTeam: TeamContext | null;
  /** Set or clear the active team. Persists to localStorage and updates
   *  the `X-Sceneforge-Team` header attached by `lib/api.ts`. */
  setActiveTeam: (team: TeamContext | null) => void;
}

const ActiveTeamContext = createContext<TeamContextValue | undefined>(undefined);

function readPersistedTeam(): TeamContext | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TeamContext>;
    if (
      typeof parsed?.team_id === 'string'
      && parsed.team_id.length > 0
      && typeof parsed.team_name === 'string'
    ) {
      return { team_id: parsed.team_id, team_name: parsed.team_name };
    }
    return null;
  } catch {
    return null;
  }
}

export function TeamProvider({ children }: { children: ReactNode }): ReactElement {
  // Important: keep `lib/api.ts`'s `ACTIVE_TEAM_ID` synchronous with the
  // provider's state. We seed it on first render via the lazy initializer
  // AND inside every `setActiveTeam` call — never via a deferred useEffect.
  // Why: child components fire their API calls in `useEffect`s that run
  // BEFORE the parent provider's `useEffect`, so a deferred sync would
  // race and the first request after team change goes out with no header
  // and gets a `team_id_required` 400.
  const [activeTeam, setActiveTeamState] = useState<TeamContext | null>(() => {
    const persisted = readPersistedTeam();
    setApiTeamId(persisted?.team_id ?? null);
    return persisted;
  });

  const setActiveTeam = useCallback((team: TeamContext | null) => {
    setApiTeamId(team?.team_id ?? null);
    setActiveTeamState(team);
    if (typeof window === 'undefined') return;
    try {
      if (team) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(team));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // localStorage can throw in private browsing / quota-exceeded — the
      // in-memory state is still authoritative for this tab, so swallow.
    }
  }, []);

  const value = useMemo<TeamContextValue>(
    () => ({ activeTeam, setActiveTeam }),
    [activeTeam, setActiveTeam],
  );

  return (
    <ActiveTeamContext.Provider value={value}>
      {children}
    </ActiveTeamContext.Provider>
  );
}

/** Hook for components that need the currently active team. Throws if
 *  used outside of `<TeamProvider>` so misuse surfaces immediately
 *  rather than as a silent "no team header" auth failure on the server. */
export function useActiveTeam(): TeamContextValue {
  const ctx = useContext(ActiveTeamContext);
  if (!ctx) {
    throw new Error('useActiveTeam must be used inside <TeamProvider>');
  }
  return ctx;
}
